import React from 'react';
import {
  ArrowRight,
  ChevronRight,
  Code2,
  Menu,
  MoveUpRight,
  X,
} from 'lucide-react';
import InteractiveHeroScene from './components/InteractiveHeroScene';
import backgroundVideo from './assets/images/background-top.mp4';
import logowhite from './assets/images/moidtechwhite.png';

const whatsappUrl = 'https://api.whatsapp.com/send/?phone=584122540247&text=Hola%20MOID%20Tech%2C%20quiero%20conversar%20sobre%20un%20proyecto.&type=phone_number&app_absent=0';

const process = [
  ['01', 'Descubrir', 'Entendemos el negocio, los usuarios y la oportunidad antes de escribir una sola línea.'],
  ['02', 'Diseñar', 'Prototipamos la experiencia y validamos las decisiones críticas con velocidad.'],
  ['03', 'Construir', 'Desarrollamos en ciclos cortos, con entregas visibles y calidad desde el código.'],
  ['04', 'Evolucionar', 'Medimos, aprendemos y mejoramos el producto después del lanzamiento.'],
];

function App() {
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const revealTargets = document.querySelectorAll('.section-intro:not(.process-intro), .service-row, .work-copy, .work-visual, .contact-section > *:not(.contact-noise)');
    const completionTimers = [];
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          completionTimers.push(window.setTimeout(() => {
            entry.target.classList.add('motion-complete');
          }, 1000));
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.16 });

    revealTargets.forEach((target) => {
      target.classList.add('motion-reveal');
      observer.observe(target);
    });

    return () => {
      observer.disconnect();
      completionTimers.forEach(window.clearTimeout);
    };
  }, []);

  React.useEffect(() => {
    const scenes = document.querySelectorAll('[data-scroll-scene]');

    const updateScenes = () => {
      scenes.forEach((scene) => {
        const rect = scene.getBoundingClientRect();
        const travel = Math.max(scene.offsetHeight - window.innerHeight, 1);
        const progress = Math.min(Math.max(-rect.top / travel, 0), 1);
        scene.style.setProperty('--scene-progress', progress.toFixed(4));
        scene.style.setProperty('--scene-inverse', (1 - progress).toFixed(4));
        scene.style.setProperty('--scene-percent', `${progress * 100}%`);

        if (scene.id === 'servicios') {
          scene.style.setProperty('--approach-arc-rotation', `${-38 + progress * 52}deg`);
          scene.style.setProperty('--approach-arc-scale', (0.78 + progress * 0.34).toFixed(4));
          scene.style.setProperty('--approach-title-y', `${progress * -5}vh`);
          scene.style.setProperty('--approach-media-y', `${(1 - progress) * 18}vh`);
          scene.style.setProperty('--approach-copy-y', `${(1 - progress) * 11}vh`);
        }

        if (scene.id === 'trabajo') {
          scene.style.setProperty('--work-rotation', `${progress * 95}deg`);
          scene.style.setProperty('--work-counter-rotation', `${progress * -95}deg`);
          scene.style.setProperty('--work-core-rotation', `${-4 - progress * 95}deg`);
          scene.style.setProperty('--work-scale', (0.78 + progress * 0.45).toFixed(4));
          scene.style.setProperty('--work-copy-x', `${progress * -7}vw`);
          scene.style.setProperty('--work-grow', `${progress * 80}vw`);
          scene.style.setProperty('--work-heading-scale', (1 - progress * 0.12).toFixed(4));
          scene.style.setProperty('--work-detail-opacity', Math.max(1 - progress * 1.5, 0).toFixed(4));
        }

        if (scene.id === 'proceso') {
          scene.style.setProperty('--process-title-y', `${progress * -7}vh`);
          scene.style.setProperty('--process-ribbon-rotation', `${-12 + progress * 19}deg`);
          scene.style.setProperty('--process-ribbon-scale', (0.84 + progress * 0.22).toFixed(4));
          scene.style.setProperty('--process-ribbon-reveal', `${Math.min(progress * 180, 100)}%`);
          scene.style.setProperty('--process-copy-opacity', Math.min(Math.max((progress - 0.18) * 3.2, 0), 1).toFixed(4));
          scene.style.setProperty('--process-copy-y', `${Math.max((0.5 - progress) * 110, 0)}px`);
          [0.48, 0.54, 0.6, 0.66].forEach((threshold, index) => {
            const cardProgress = Math.min(Math.max((progress - threshold) / 0.24, 0), 1);
            scene.style.setProperty(`--process-card-${index + 1}-y`, `${(1 - cardProgress) * 42}vh`);
          });
        }

        if (scene.id === 'contacto') {
          scene.style.setProperty('--contact-rotation', `${(1 - progress) * 7}deg`);
          scene.style.setProperty('--contact-scale', (0.86 + progress * 0.14).toFixed(4));
          scene.style.setProperty('--contact-title-y', `${(1 - progress) * 70}px`);
          scene.style.setProperty('--contact-button-y', `${(1 - progress) * 100}px`);
        }
      });
    };

    updateScenes();
    window.addEventListener('scroll', updateScenes, { passive: true });
    window.addEventListener('resize', updateScenes);

    return () => {
      window.removeEventListener('scroll', updateScenes);
      window.removeEventListener('resize', updateScenes);
    };
  }, []);

  return (
    <div className="moid-site">
      <header className="site-header">
        <a className="brand footer-brand" href="/#inicio">
            <img src={logowhite} alt="MOID Tech" width="90" height="24" />
        </a>

        <nav className={`main-nav ${menuOpen ? 'is-open' : ''}`} aria-label="Navegación principal">
          <a href="#servicios" onClick={() => setMenuOpen(false)}>Servicios</a>
          <a href="#proceso" onClick={() => setMenuOpen(false)}>Proceso</a>
          <a href="#trabajo" onClick={() => setMenuOpen(false)}>Trabajo</a>
          <a href="#contacto" onClick={() => setMenuOpen(false)}>Contacto</a>
        </nav>

        <a className="header-cta" href={whatsappUrl} target="_blank" rel="noreferrer">
          Iniciar proyecto <MoveUpRight size={16} />
        </a>
        <button
          className="menu-button"
          type="button"
          aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </header>

      <main>
        <section className="hero" id="inicio">
          <div className="hero-grid" aria-hidden="true" />
          <InteractiveHeroScene />
          <div className="hero-copy">
            <div className="eyebrow"><span /> Software partner · LATAM</div>
            <h1><span>Ideas digitales.</span><br /><em>En movimiento.</em></h1>
            <p>Estrategia, diseño e ingeniería trabajando como un solo equipo para lanzar software que la gente quiere usar.</p>
            <div className="hero-actions">
              <a className="button button-primary" href={whatsappUrl} target="_blank" rel="noreferrer">
                Construyamos algo <ArrowRight size={18} />
              </a>
              <a className="text-link" href="#trabajo">Explorar nuestro enfoque <ChevronRight size={17} /></a>
            </div>
          </div>
          <div className="hero-proof">
            <span>Un equipo. Todo el producto.</span>
            <div><b>ESTRATEGIA</b><b>DISEÑO</b><b>DESARROLLO</b><b>ESCALA</b></div>
          </div>
          <a className="scroll-cue" href="#servicios"><span>Scroll para explorar</span><i /></a>
        </section>

        <section className="services-section approach-section" id="servicios" data-scroll-scene>
          <div className="approach-sticky">
            <div className="approach-arc" aria-hidden="true"><i /></div>
            <div className="approach-title">
              <span className="section-index">01 / NUESTRA FORMA DE CREAR</span>
              <h2>Ideas audaces,<br /><em>hechas producto.</em></h2>
            </div>
            <div className="approach-media">
              <video src={backgroundVideo} autoPlay loop muted playsInline aria-label="Experiencia digital creada por MOID Tech" />
              <div className="approach-media-tint" />
              <div className="approach-media-caption"><span>MOID / PRODUCT LAB</span><b>Sistemas digitales en movimiento</b></div>
            </div>
            <div className="approach-copy">
              <p>Combinamos estrategia, diseño, ingeniería y movimiento para crear productos digitales que se sienten diferentes y funcionan sin fricción.</p>
              <a className="approach-link" href="#trabajo"><span /> Nuestro enfoque <ArrowRight size={16} /></a>
              <div className="approach-capabilities"><span>PRODUCTO</span><span>UX/UI</span><span>DESARROLLO</span><span>CLOUD</span></div>
            </div>
            <div className="approach-counter" aria-hidden="true"><span>01</span><i /><b>04</b></div>
          </div>
        </section>

        <section className="work-section" id="trabajo" data-scroll-scene>
          <div className="work-sticky">
            <div className="work-copy">
              <span className="section-index">02 / IMPACTO</span>
              <h2>Software que mueve<br />métricas, no solo <em>píxeles.</em></h2>
              <p>Nos integramos como parte de tu equipo. Pensamos en resultados, cuestionamos lo obvio y construimos para el largo plazo.</p>
              <a className="text-link light" href={whatsappUrl} target="_blank" rel="noreferrer">Hablemos de tu reto <ArrowRight size={18} /></a>
            </div>
            <div className="work-visual">
              <div className="orbit orbit-one" /><div className="orbit orbit-two" />
              <div className="impact-core"><Code2 /><strong>Ideas</strong><span>→ producto</span></div>
              <div className="impact-stat stat-a"><strong>2.4×</strong><span>más velocidad</span></div>
              <div className="impact-stat stat-b"><strong>38%</strong><span>menos operación</span></div>
              <div className="impact-stat stat-c"><strong>24/7</strong><span>productos activos</span></div>
            </div>
          </div>
        </section>

        <section className="process-section" id="proceso" data-scroll-scene>
          <div className="process-sticky">
            <div className="process-ribbon" aria-hidden="true"><i /></div>
            <div className="section-intro process-intro">
              <span className="section-index">03 / PROCESO</span>
              <div><h2>Donde las ideas<br />se convierten en<br /><em>experiencias.</em></h2></div>
            </div>
            <div className="process-editorial">
              <p>No perseguimos tendencias ni construimos como todos los demás. Convertimos retos complejos en productos claros, útiles y memorables.</p>
              <span>DEL CONCEPTO AL PRODUCTO</span>
            </div>
            <div className="process-grid">
              {process.map(([number, title, text]) => (
                <article key={title}>
                  <span>{number}</span>
                  <div className="process-mark" />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="contact-section" id="contacto" data-scroll-scene>
          <div className="contact-noise" aria-hidden="true" />
          <span className="section-index">04 / SIGUIENTE PASO</span>
          <h2 className='mt-8'>Tu próxima gran idea<br />merece salir del papel.</h2>
          <p>Cuéntanos dónde quieres llegar. Nosotros trazamos el camino tecnológico.</p>
          <a className="button button-dark" href={whatsappUrl} target="_blank" rel="noreferrer">
            Empecemos a construir <ArrowRight size={19} />
          </a>
        </section>
      </main>

      <footer className="new-footer">
        <div className="footer-bottom">
          <a className="brand footer-brand" href="/#inicio">
            <img src={logowhite} alt="MOID Tech" width="90" height="24" />
          </a>
          <p>
            <nav className="footer-socials" aria-label="Redes sociales">
            <a href="/#inicio" target="_blank" rel="noreferrer">Transformando ideas en soluciones tecnológicas.</a>
          </nav>
          </p>
          <nav className="footer-socials" aria-label="Redes sociales">
            <a href="https://www.instagram.com/moid.tech/" target="_blank" rel="noreferrer">Instagram</a>
            <a href="https://www.linkedin.com/in/moid-tech/" target="_blank" rel="noreferrer">LinkedIn</a>
          </nav>
          <a className="footer-email" href="mailto:hola@moid.tech">hola@moid.tech</a>
          <nav className="footer-legal-links" aria-label="Enlaces legales">
            <a href="/terms-and-conditions">Términos</a>
            <a href="/privacy-policy">Privacidad</a>
          </nav>
          <a className="footer-back-top" href="/#inicio">Volver arriba <span>↑</span></a>
          <span>© {new Date().getFullYear()} MOID Tech</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
